import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import useMediaQuery from '@mui/material/useMediaQuery'

import { theme } from 'styles/theme'

import { MobileCardSkeleton } from './MobileCardSkeleton'

// Mesma largura que o <Radio>/<Checkbox> da tabela real ocupa, para o
// skeleton não deslocar as colunas quando os dados chegam.
const SELECT_COLUMN_WIDTH = 42

/** `actions` = coluna com várias ações; `icon` = coluna com uma só. */
export type CellRenderHint =
  | 'text'
  | 'avatar'
  | 'chip'
  | 'actions'
  | 'stacked'
  | 'icon'

const DEFAULT_ACTIONS_COUNT = 3

type TableSkeletonProps = {
  columns?: number
  rows?: number
  columnWidths?: string[]
  cellRenderHint?: CellRenderHint[]
  /** Esconde o bloco de paginação — para tabelas com `hiddePagination`. */
  hidePagination?: boolean
  /** Reserva a coluna de radio/checkbox das tabelas com `useSelect`. */
  showSelect?: boolean
  /** Quantos ícones a coluna `actions` desenha — espelha a tabela real. */
  actionsCount?: number
  /**
   * Mantém o skeleton em formato de tabela no mobile. Só para tabelas que não
   * viram card (a prop `mobileCards` desligada).
   */
  keepTableOnMobile?: boolean
}

const renderCell = (
  hint: CellRenderHint,
  actionsCount: number = DEFAULT_ACTIONS_COUNT
) => {
  switch (hint) {
    case 'actions':
      return (
        <Box display="flex" gap={0.5} justifyContent="flex-end">
          {Array.from({ length: actionsCount }).map((_, index) => (
            <Skeleton
              key={`action-${index}`}
              variant="circular"
              width={24}
              height={24}
              animation="wave"
              sx={{ bgcolor: theme.palette.neutral[800] }}
            />
          ))}
        </Box>
      )
    // Célula de duas linhas (rótulo em cima, valor embaixo). Sem isso o
    // skeleton anuncia uma linha só e a tabela cresce quando os dados chegam.
    case 'stacked':
      return (
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Skeleton
            variant="text"
            width="40%"
            height={14}
            animation="wave"
            sx={{ bgcolor: theme.palette.neutral[700] }}
          />
          <Skeleton
            variant="text"
            width="70%"
            height={18}
            animation="wave"
            sx={{ bgcolor: theme.palette.neutral[700] }}
          />
        </Box>
      )
    case 'icon':
      return (
        <Box display="flex" justifyContent="flex-end">
          <Skeleton
            variant="circular"
            width={24}
            height={24}
            animation="wave"
            sx={{ bgcolor: theme.palette.neutral[800] }}
          />
        </Box>
      )
    case 'chip':
      return (
        <Skeleton
          variant="rounded"
          width={70}
          height={24}
          animation="wave"
          sx={{
            bgcolor: theme.palette.neutral[700],
            borderRadius: '9999px'
          }}
        />
      )
    case 'avatar':
      return (
        <Box display="flex" gap={1} alignItems="center">
          <Skeleton
            variant="circular"
            width={28}
            height={28}
            animation="wave"
            sx={{ bgcolor: theme.palette.neutral[700] }}
          />
          <Skeleton
            variant="text"
            width="60%"
            height={18}
            animation="wave"
            sx={{ bgcolor: theme.palette.neutral[700] }}
          />
        </Box>
      )
    case 'text':
    default:
      return (
        <Skeleton
          variant="text"
          width="75%"
          height={18}
          animation="wave"
          sx={{ bgcolor: theme.palette.neutral[700] }}
        />
      )
  }
}

export const TableSkeleton = ({
  columns = 6,
  rows = 5,
  columnWidths,
  cellRenderHint,
  hidePagination = false,
  showSelect = false,
  actionsCount = DEFAULT_ACTIONS_COUNT,
  keepTableOnMobile = false
}: TableSkeletonProps) => {
  const isMobile = useMediaQuery('(max-width: 600px)', { noSsr: false })

  // No mobile a tabela vira card, então o skeleton precisa ter a forma do card
  // — senão o loading aparece como tabela e a lista "pula" quando os dados
  // chegam.
  if (isMobile && !keepTableOnMobile) {
    const hints =
      cellRenderHint && cellRenderHint.length === columns ? cellRenderHint : []
    return (
      <MobileCardSkeleton
        cards={Math.min(rows, 4)}
        rowsPerCard={Math.max(1, columns - 3)}
        actionsCount={actionsCount}
        hideActions={hints.length > 0 && !hints.includes('actions')}
        hidePagination={hidePagination}
      />
    )
  }

  const widths =
    columnWidths && columnWidths.length === columns
      ? columnWidths
      : Array.from({ length: columns }, () => `${100 / columns}%`)

  const hints: CellRenderHint[] =
    cellRenderHint && cellRenderHint.length === columns
      ? cellRenderHint
      : Array.from({ length: columns }, (_, i) =>
          columns >= 3 && i === columns - 1 ? 'actions' : 'text'
        )

  return (
    <Box width="100%">
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                borderBottom: `1px solid ${theme.palette.neutral[700]}`
              }}
            >
              {showSelect && (
                <TableCell
                  sx={{
                    width: SELECT_COLUMN_WIDTH,
                    py: 2,
                    borderBottom: 'none'
                  }}
                />
              )}
              {Array.from({ length: columns }).map((_, index) => (
                <TableCell
                  key={`header-${index}`}
                  sx={{
                    width: widths[index] || 'auto',
                    py: 2,
                    borderBottom: 'none'
                  }}
                >
                  <Skeleton
                    variant="text"
                    width={index === 0 ? '60%' : '80%'}
                    height={20}
                    animation="wave"
                    sx={{ bgcolor: theme.palette.neutral[700] }}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <TableRow
                key={`row-${rowIndex}`}
                sx={{
                  borderBottom: `1px solid ${theme.palette.neutral[800]}`,
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                {showSelect && (
                  <TableCell
                    sx={{
                      width: SELECT_COLUMN_WIDTH,
                      py: 2.5,
                      borderBottom: 'none'
                    }}
                  >
                    <Box display="flex" justifyContent="center">
                      <Skeleton
                        variant="circular"
                        width={20}
                        height={20}
                        animation="wave"
                        sx={{ bgcolor: theme.palette.neutral[800] }}
                      />
                    </Box>
                  </TableCell>
                )}
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <TableCell
                    key={`cell-${rowIndex}-${colIndex}`}
                    sx={{
                      py: 2.5,
                      borderBottom: 'none'
                    }}
                  >
                    {renderCell(hints[colIndex], actionsCount)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!hidePagination && (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          mt={2}
          pr={2}
        >
          <Skeleton
            variant="text"
            width={100}
            height={24}
            animation="wave"
            sx={{ bgcolor: theme.palette.neutral[700] }}
          />
          <Box display="flex" gap={0.5}>
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              animation="wave"
              sx={{ bgcolor: theme.palette.neutral[800] }}
            />
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              animation="wave"
              sx={{ bgcolor: theme.palette.neutral[800] }}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}
